import { useEffect, useMemo, useState } from 'react'
import { locationCardBackdrop } from '../game/locationBackgrounds'
import { getPortraitSrc } from '../game/portraits'
import { useI18n } from '../i18n/useI18n'
import { expeditionParticipantIds, LOCATIONS } from '../game/locations'
import { getVehicleDef } from '../game/vehicles'
import { useGameStore } from '../store/useGameStore'
import type { Expedition, Survivor } from '../game/types'

type Props = {
  expedition: Expedition
  survivors: Survivor[]
}

const formatRemaining = (ms: number) => {
  const total = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

export function ExpeditionCard({ expedition, survivors }: Props) {
  const { language, t, pick } = useI18n()
  const team = expeditionParticipantIds(expedition)
    .map((id) => survivors.find((s) => s.id === id))
    .filter((s): s is Survivor => !!s)
  const teamIdsKey = team.map((s) => s.id).join('|')
  const gems = useGameStore((s) => s.gems)
  const skipExpeditionHalfTime = useGameStore((s) => s.skipExpeditionHalfTime)
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 500)
    return () => window.clearInterval(id)
  }, [])

  const loc = LOCATIONS[expedition.locationId]
  const total = expedition.endsAt - expedition.startedAt
  const remaining = expedition.endsAt - now
  const elapsedPct = Math.max(
    0,
    Math.min(100, ((total - remaining) / total) * 100),
  )

  const diary = expedition.diary
  const diaryIndex = elapsedPct < 33 ? 0 : elapsedPct < 66 ? 1 : 2
  const currentLine = diary[Math.min(diaryIndex, diary.length - 1)] ?? ''

  const awayLabel = useMemo(() => {
    if (team.length === 0) return t('exp_card_unknown')
    const andWord = language === 'nl' ? 'en' : 'and'
    const names = team.map((s) => s.name)
    if (names.length <= 3) {
      if (names.length === 1) return names[0]
      if (names.length === 2) return `${names[0]} ${andWord} ${names[1]}`
      return `${names[0]}, ${names[1]} ${andWord} ${names[2]}`
    }
    return `${names.slice(0, 3).join(', ')} +${names.length - 3}`
  }, [language, team, t])
  const backdrop = locationCardBackdrop(expedition.locationId)
  const quotePortraitSurvivor = useMemo(() => {
    if (team.length === 0) return undefined
    let seed = 0
    const key = `${expedition.id}:${diaryIndex}:${teamIdsKey}`
    for (let i = 0; i < key.length; i++) seed = (seed * 31 + key.charCodeAt(i)) >>> 0
    const idx = seed % team.length
    return team[idx]
  }, [diaryIndex, expedition.id, team, teamIdsKey])
  const portraitSrc = getPortraitSrc(quotePortraitSurvivor?.portrait)
  const survivorInitials = quotePortraitSurvivor?.name.slice(0, 2).toUpperCase() ?? '??'

  const vehicleGlyphs = useMemo(() => {
    const ids =
      expedition.vehicleDefIds && expedition.vehicleDefIds.length > 0
        ? expedition.vehicleDefIds
        : expedition.vehicleDefId
          ? [expedition.vehicleDefId]
          : []
    return ids.map((id) => getVehicleDef(id)?.glyph).filter((g): g is string => !!g)
  }, [expedition.vehicleDefId, expedition.vehicleDefIds])

  return (
    <div className="relative overflow-hidden rounded-md border border-amber-900/40 bg-stone-800/70 p-2.5">
      <img
        src={backdrop.src}
        alt=""
        aria-hidden
        decoding="async"
        className="pointer-events-none absolute inset-0 z-0 h-full w-full object-cover select-none"
        style={{ filter: backdrop.brightnessFilter }}
      />
      <div className="absolute inset-0 z-1 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.18),rgba(0,0,0,0.86))]" />
      <div className="relative z-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-stone-100">
            {pick(loc.name)}
          </div>
          <div className="text-xs text-stone-500">{t('exp_card_away', { name: awayLabel })}</div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => skipExpeditionHalfTime(expedition.id)}
            disabled={gems < 1}
            className="flex items-center gap-0.5 rounded border border-purple-800/60 bg-purple-950/40 px-1.5 py-0.5 text-[10px] text-purple-300 transition hover:bg-purple-900/50 disabled:cursor-not-allowed disabled:opacity-40"
            title={t('exp_skip_half_time_title')}
          >
            <span>◆</span>
            <span>1</span>
          </button>
          <div className="font-mono text-base text-amber-300">
            {formatRemaining(remaining)}
          </div>
        </div>
      </div>
      <div
        className={`relative z-2 mt-1.5 ${vehicleGlyphs.length > 0 ? 'h-4' : 'h-1.5'}`}
        style={vehicleGlyphs.length > 0 ? { perspective: '90px' } : undefined}
      >
        <div
          className={`absolute inset-x-0 rounded-sm bg-stone-800 ring-1 ring-inset ring-stone-700 ${
            vehicleGlyphs.length > 0
              ? 'top-1/2 h-1.5 -translate-y-1/2 overflow-hidden'
              : 'inset-y-0 overflow-hidden'
          }`}
        >
          <div
            className="h-full bg-amber-600 transition-[width] duration-500 ease-linear"
            style={{ width: `${elapsedPct}%` }}
          />
        </div>
        {vehicleGlyphs.length > 0 && (
          <div className="pointer-events-none absolute inset-0">
            {vehicleGlyphs.map((glyph, i) => {
              const spread = (i - (vehicleGlyphs.length - 1) / 2) * 2.5
              const dy = i * -1.2
              const dz = i * 1.5
              return (
                <span
                  key={`${glyph}-${i}`}
                  aria-hidden
                  className="absolute top-1/2 flex h-[18px] w-[18px] items-center justify-center text-[13px] leading-none drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)] select-none"
                  style={{
                    left: `${elapsedPct}%`,
                    transform: `translate3d(calc(-50% + ${spread}px), calc(-50% + ${dy}px), ${dz}px)`,
                    transition: 'left 500ms linear, transform 500ms linear',
                    zIndex: 10 + i,
                  }}
                >
                  {glyph}
                </span>
              )
            })}
          </div>
        )}
      </div>
      {currentLine && (
        <div className="relative z-2 mt-1.5 flex items-start gap-1.5">
          <div className="h-5 w-5 shrink-0 overflow-hidden rounded-sm border border-stone-700 bg-linear-to-br from-stone-800 to-stone-950">
            {portraitSrc ? (
              <img
                src={portraitSrc}
                alt={quotePortraitSurvivor?.name ?? t('exp_card_unknown')}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="grid h-full w-full place-items-center font-mono text-[8px] font-bold text-stone-300">
                {survivorInitials}
              </div>
            )}
          </div>
          <p className="min-w-0 text-[11px] italic leading-snug text-stone-500">
            &ldquo;{currentLine}&rdquo;
          </p>
        </div>
      )}
    </div>
  )
}
